import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { differenceInYears, isFuture, isValid, parse } from 'date-fns';
import { QuestionnaireService } from '../../../services';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { loadUserInformation, selectUserInformation } from '../../../store/reducers';
import type { Address, Patient, Questionnaire as QuestionnaireData } from '../../../types/ninox.types';
import { Loading } from '../../atoms';
import { Form, ProgressStep, ProgressSteps } from '../../organisms';
import { patientFields } from './patient.data';
import { questionnaireFields } from './questionnaire.data';

const UNDERAGE_ERROR_MESSAGE = 'Sie müssen mindestens 18 Jahre alt sein um eine Therapie bei nowomed zu beginnen.';
const QUESTIONNAIRE_ALREADY_SUBMITTED_MESSAGE = 'Sie haben den Fragebogen bereits eingereicht.';

export function Questionnaire(): JSX.Element {
  const navigation = useNavigate();
  const dispatch = useAppDispatch();
  const userInformation = useAppSelector(selectUserInformation);

  const [loading, setLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [questionnaireData, setQuestionnaireData] = useState<QuestionnaireData | undefined>(undefined);

  useEffect(() => {
    if (userInformation.questionnaire && userInformation.questionnaire.length > 0) {
      navigation('/dashboard');
    }
    if (userInformation) {
      setLoading(false);
    }
  }, [userInformation]);

  const sendQuestionnaire = async (patientData: Patient & Address) => {
    if (!questionnaireData || !patientData || isSubmitting) {
      return;
    }

    const { firstname, lastname, sex, birthday, nationality, insurance, street, houseNumber, postcode, city } =
      patientData;
    if (!birthday) {
      toast.error(UNDERAGE_ERROR_MESSAGE);

      return;
    }

    const date = parse(birthday, 'yyyy-MM-dd', new Date());
    const age = differenceInYears(new Date(), date);
    if (!isValid(date) || isFuture(date) || age < 18) {
      toast.error(UNDERAGE_ERROR_MESSAGE);

      return;
    }

    setIsSubmitting(true);
    const res = await QuestionnaireService.createQuestionnaire({
      questionnaire: questionnaireData,
      patientData: {
        firstname,
        lastname,
        sex,
        birthday,
        nationality,
        insurance,
      },
      addressData: {
        street,
        houseNumber,
        postcode,
        city,
        country: 'germany',
      },
    });
    if (res.error) {
      toast.error(res.status === 409 ? QUESTIONNAIRE_ALREADY_SUBMITTED_MESSAGE : res.message);
      setIsSubmitting(false);

      return console.error('Error submitting questionnaire', res.message);
    }
    dispatch(loadUserInformation);
    navigation('/dashboard');
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <div style={{ margin: '0 auto', maxWidth: '300px' }}>
        <ProgressSteps>
          <ProgressStep step={1} title='Medizinische Fragen' active />
          <ProgressStep step={2} title='Persönliche Daten' active={!!questionnaireData} />
        </ProgressSteps>
      </div>
      {!questionnaireData ? (
        <Form key='questionnaire' fields={questionnaireFields} onSubmit={setQuestionnaireData} />
      ) : (
        <Form
          key='patient'
          fields={patientFields}
          onSubmit={sendQuestionnaire}
          data={{ nationality: 'german', country: 'germany' }}
          button={isSubmitting ? 'Wird übermittelt...' : 'Abschließen'}
          disabled={isSubmitting}
        />
      )}
    </>
  );
}
